import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, CalendarIcon, Upload, FileText, X, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

export default function FileClaim() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [files, setFiles] = useState([]);
  const [claimId, setClaimId] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    policy_id: "",
    job_loss_date: null,
    last_employer: "",
    reason_for_job_loss: ""
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  useEffect(() => {
    if (formData.policy_id) {
      checkEligibility(formData.policy_id);
    } else {
      setEligibility(null);
    }
  }, [formData.policy_id]);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/policies/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activePolicies = response.data.filter(p => p.status === "active");
      setPolicies(activePolicies);
      if (activePolicies.length === 1) {
        setFormData(prev => ({ ...prev, policy_id: activePolicies[0].policy_id }));
      }
    } catch (error) {
      toast.error("Failed to load policies");
    } finally {
      setLoading(false);
    }
  };

  const checkEligibility = async (policyId) => {
    setCheckingEligibility(true);
    try {
      const response = await axios.get(`${API}/claims/eligibility/${policyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibility(response.data);
    } catch (error) {
      console.error("Failed to check eligibility", error);
      setEligibility(null);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check eligibility before submitting
    if (eligibility && !eligibility.is_eligible) {
      toast.error(eligibility.message || "You are not eligible to file a claim for this policy.");
      return;
    }
    
    setSubmitting(true);

    try {
      // First, create the claim
      const claimResponse = await axios.post(`${API}/claims/`, {
        policy_id: formData.policy_id,
        job_loss_date: formData.job_loss_date ? format(formData.job_loss_date, "yyyy-MM-dd") : "",
        last_employer: formData.last_employer,
        reason_for_job_loss: formData.reason_for_job_loss
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newClaimId = claimResponse.data.claim_id;
      setClaimId(newClaimId);

      // Then upload documents if any
      if (files.length > 0) {
        for (const file of files) {
          const formDataObj = new FormData();
          formDataObj.append("file", file);
          
          await axios.post(`${API}/claims/${newClaimId}/documents`, formDataObj, {
            headers: { 
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          });
        }
      }

      toast.success("Claim filed successfully!");
      navigate(`/claims/${newClaimId}`);
    } catch (error) {
      // Handle structured error response
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail.error === 'eligibility_not_met') {
        toast.error(errorDetail.message);
        setEligibility({
          is_eligible: false,
          months_paid: errorDetail.months_paid,
          months_required: errorDetail.months_required,
          remaining_months: errorDetail.remaining_months,
          message: errorDetail.message
        });
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : "Failed to file claim");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <header className="glass-header sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center gap-2">
                <NestIcon className="h-8 w-8 text-primary" />
                <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                  TheRentPayer
                </span>
              </Link>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          <Card className="card-organic">
            <CardContent className="p-12 text-center">
              <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
                No Active Policy Found
              </h3>
              <p className="text-muted-foreground mb-6">
                You need an active insurance policy to file a claim. Get a quote and activate your coverage first.
              </p>
              <Link to="/quote">
                <Button className="btn-primary">Get a Quote</Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
            File a Claim
          </h1>
          <p className="text-muted-foreground">
            Submit your job loss claim for rent coverage
          </p>
        </div>

        <Card className="card-organic">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Policy Selection */}
              <div className="space-y-2">
                <Label>Select Policy</Label>
                <Select 
                  value={formData.policy_id} 
                  onValueChange={(value) => setFormData({ ...formData, policy_id: value })}
                >
                  <SelectTrigger data-testid="policy-select">
                    <SelectValue placeholder="Select a policy" />
                  </SelectTrigger>
                  <SelectContent>
                    {policies.map((policy) => (
                      <SelectItem key={policy.policy_id} value={policy.policy_id}>
                        {policy.policy_id} - ${policy.monthly_rent}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Eligibility Status */}
              {formData.policy_id && (
                <div className={`rounded-lg p-4 border ${
                  checkingEligibility ? 'bg-gray-50 border-gray-200' :
                  eligibility?.is_eligible ? 'bg-green-50 border-green-200' :
                  'bg-amber-50 border-amber-200'
                }`}>
                  {checkingEligibility ? (
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      <span className="text-muted-foreground">Checking eligibility...</span>
                    </div>
                  ) : eligibility?.is_eligible ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Eligible to File Claim</p>
                        <p className="text-sm text-green-700">
                          You have {eligibility.months_paid} month(s) of payments. You meet the 6-month requirement.
                        </p>
                      </div>
                    </div>
                  ) : eligibility?.has_existing_claim ? (
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Active Claim Exists</p>
                        <p className="text-sm text-amber-700">
                          You already have an active claim for this policy ({eligibility.existing_claim_id}).
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Clock className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-800">Not Yet Eligible</p>
                          <p className="text-sm text-amber-700">
                            {eligibility?.message || `You need ${eligibility?.remaining_months || 6} more month(s) of payments.`}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-amber-700">Payment Progress</span>
                          <span className="font-medium text-amber-800">
                            {eligibility?.months_paid || 0} / {eligibility?.months_required || 12} months
                          </span>
                        </div>
                        <Progress 
                          value={((eligibility?.months_paid || 0) / (eligibility?.months_required || 12)) * 100} 
                          className="h-2 bg-amber-200"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Job Loss Date */}
              <div className="space-y-2">
                <Label>Date of Job Loss</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="job-loss-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.job_loss_date ? format(formData.job_loss_date, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.job_loss_date}
                      onSelect={(date) => setFormData({ ...formData, job_loss_date: date })}
                      disabled={(date) => date > new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Last Employer */}
              <div className="space-y-2">
                <Label htmlFor="last_employer">Last Employer</Label>
                <Input
                  id="last_employer"
                  value={formData.last_employer}
                  onChange={(e) => setFormData({ ...formData, last_employer: e.target.value })}
                  placeholder="Company name"
                  required
                  data-testid="last-employer-input"
                />
              </div>

              {/* Reason for Job Loss */}
              <div className="space-y-2">
                <Label htmlFor="reason_for_job_loss">Reason for Job Loss</Label>
                <Textarea
                  id="reason_for_job_loss"
                  value={formData.reason_for_job_loss}
                  onChange={(e) => setFormData({ ...formData, reason_for_job_loss: e.target.value })}
                  placeholder="Please describe the circumstances of your job loss..."
                  rows={4}
                  required
                  data-testid="job-loss-reason-textarea"
                />
              </div>

              {/* Document Upload */}
              <div className="space-y-2">
                <Label>Supporting Documents</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Upload termination letter, unemployment documents, or other proof of job loss
                </p>
                
                <div 
                  className="upload-zone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    data-testid="file-upload-input"
                  />
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF, DOC, DOCX, JPG, PNG (max 10MB each)
                  </p>
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-muted rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="icon" 
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Important</p>
                    <p className="text-amber-700">
                      Please ensure all information provided is accurate. False claims may result in policy cancellation.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full py-6"
                disabled={submitting || !formData.policy_id || !formData.job_loss_date || (eligibility && !eligibility.is_eligible)}
                data-testid="submit-claim-btn"
              >
                {submitting ? "Submitting Claim..." : eligibility && !eligibility.is_eligible ? "Not Eligible Yet" : "Submit Claim"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
